import React from 'react';

/** Skeleton placeholder for loading states — shimmer animation via CSS */
export const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton ${className}`} />
);

/** Skeleton for chapter cards in LearnView */
export const LearnViewSkeleton: React.FC = () => (
  <div className="p-4 space-y-4">
    {/* Progress bar skeleton */}
    <div className="skeleton h-3 w-full rounded-full" />
    <div className="skeleton h-5 w-32 mt-2" />
    
    {/* Chapter cards */}
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="bg-[var(--f-surface)] rounded-2xl p-4 space-y-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="skeleton h-10 w-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-3/4" />
            <div className="skeleton h-3 w-1/2" />
          </div>
        </div>
        <div className="skeleton h-2 w-full rounded-full" />
      </div>
    ))}
  </div>
);

/** Skeleton for leaderboard entries */
export const LeaderboardSkeleton: React.FC = () => (
  <div className="p-4 space-y-3">
    {/* Top 3 podium */}
    <div className="flex justify-center gap-4 mb-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex flex-col items-center gap-2">
          <div className="skeleton h-14 w-14 rounded-full" />
          <div className="skeleton h-3 w-16" />
          <div className="skeleton h-3 w-12" />
        </div>
      ))}
    </div>
    {/* List entries */}
    {[4, 5, 6, 7, 8].map(i => (
      <div key={i} className="flex items-center gap-3 bg-[var(--f-surface)] rounded-xl p-3">
        <div className="skeleton h-4 w-4" />
        <div className="skeleton h-9 w-9 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <div className="skeleton h-3.5 w-24" />
          <div className="skeleton h-2.5 w-16" />
        </div>
        <div className="skeleton h-4 w-12" />
      </div>
    ))}
  </div>
);

/** Skeleton for chat messages */
export const ChatSkeleton: React.FC = () => (
  <div className="p-4 space-y-4">
    {/* Welcome message */}
    <div className="flex gap-3">
      <div className="skeleton h-8 w-8 rounded-full flex-shrink-0" />
      <div className="space-y-2 flex-1">
        <div className="skeleton h-4 w-full" />
        <div className="skeleton h-4 w-3/4" />
        <div className="skeleton h-4 w-1/2" />
      </div>
    </div>
    {/* Suggestion chips */}
    <div className="flex flex-wrap gap-2 mt-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton h-8 w-28 rounded-full" />
      ))}
    </div>
  </div>
);

/** Skeleton for profile page */
export const ProfileSkeleton: React.FC = () => (
  <div className="p-4 space-y-5 pb-24">
    {/* Avatar + Name */}
    <div className="flex flex-col items-center gap-3 pt-4">
      <div className="skeleton h-20 w-20 rounded-full" />
      <div className="skeleton h-5 w-32" />
      <div className="skeleton h-3 w-24" />
    </div>
    {/* Stats Grid */}
    <div className="grid grid-cols-3 gap-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-[var(--f-surface)] rounded-2xl p-3 space-y-2">
          <div className="skeleton h-6 w-12 mx-auto" />
          <div className="skeleton h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>
    {/* Settings blocks */}
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="bg-[var(--f-surface)] rounded-2xl p-4 flex items-center gap-3">
        <div className="skeleton h-10 w-10 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-4 w-2/3" />
          <div className="skeleton h-3 w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

export default { LearnViewSkeleton, LeaderboardSkeleton, ChatSkeleton, ProfileSkeleton, SkeletonBlock };
